import { Box, Paper, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import React, { Fragment, useEffect, useState } from 'react'
import { Status } from '../../hooks/useFetchState'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { AsnReturnOrderDetailReturnBatches, createAsn } from './api'
import { Filter } from '../_WMS/FilterComponent'
import { usePagination } from '../../hooks/usePagination'
import {
  AsnCreateDto,
  AsnDetailsCreateDto,
  OrderDetailReturnReasonCreateDto,
  OrderDetailReturnReasonsBatchInfoDto,
  OrderDetailReturnReasonsCreateDto,
  RelatedBatch
} from './types'
import { Modal } from '../_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { SimpleBatchSelector } from './components/SimpleBatchSelector'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { AsnReturnDto } from './SearchReturn'
import { eAsnState } from '../../enums/eAsnState'
import { eTypeAsnDocument } from '../../enums/eTypeAsnDocument'
import { eTypeAsnSource } from '../../enums/eTypeAsnSource'
import { eTypeAsnProcess } from '../../enums/eTypeAsnProcess'
import { useSuppliers } from '../../hooks'
import { useSuppliersOrigin } from '../../hooks/useSuppliersOrigin'
import { eTypeAsnIncome } from '../../enums/eTypeAsnIncome'
import { LoadingButton } from '../_EOS/Button/LoadingButton'
import { GenericTableV2, TableColumn } from '../_EOS/GenericTable'
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { formatExpirationDate } from '../../utils/dateHelper'
import { SuppliersDDL } from '../Supplier/SuppliersDDL'
import { Loading } from '../UI/Loading/Loading'
import { SupplierOriginDDL } from '../SupplierOrigin/SupplierOriginDDL'

export interface ReturnBatchSelecionProps {
  selectedOrderId: number
  selectedTypeAsnIncomeId: number
  handleStep: React.Dispatch<React.SetStateAction<number>>
  handleSnackbar: (msg: string | undefined, status: boolean) => void
  newPreInbound: boolean
  selectedAsnReturnDto: AsnReturnDto | undefined
  userId: number
  setSelectedAsnId: React.Dispatch<React.SetStateAction<number>>
  setSelectedAsnNumber: React.Dispatch<React.SetStateAction<string>>
  setselectedAsnStateId: React.Dispatch<React.SetStateAction<number>>
  selectedWarehouseId: number
  selectedPreInboundId: number
  selectedTrackingNumber: string
  selectedCourier: string
}

// Interfaz para los lotes seleccionados
interface SelectedBatchInfo {
  rowId: string // Nuevo: identificador único de fila
  orderDetailId: number
  batchId: number
  batchNumber: string
  expirationDate?: string
}

type OrderDetailWithId = OrderDetailReturnReasonsBatchInfoDto & {
  tempId: string
}

// 2. Crea una función helper para generar el rowId único
const generateRowId = (
  row: OrderDetailReturnReasonsBatchInfoDto & { tempId?: string },
  index: number
): string => {
  // Valida si el objeto ya trae el tempId, Si no, usa el índice (como fallback).
  return (
    row.tempId || `${row.orderDetailId}_${index}_${row.returnReasonId || ''}`
  )
}

export const generateTableHeaders = (items: SelectedBatchInfo[]) => {
  const tableHeader: TableColumn<OrderDetailWithId>[] = [
    {
      key: 'sku',
      title: 'SKU',
      sortable: false
    },
    {
      key: 'quantity',
      title: 'Cantidad',
      sortable: false
    },
    {
      key: 'returnReason',
      title: 'Motivo',
      sortable: false
    },
    {
      key: 'orderDetailId',
      title: 'Fecha/Lote Seleccionado',
      headerTooltip: 'El sistema selecciona automáticamente el lote...',
      sortable: false,
      render: (_: unknown, row: OrderDetailWithId) => {
        const rowId = row.tempId

        const result = items?.find((el) => el.rowId === rowId)

        if (!result) return ''

        return (
          result.batchNumber ||
          formatExpirationDate(result.expirationDate, 'dd-MM-yyyy')
        )
      }
    }
  ]

  return tableHeader
}

// Selección de Información y Creación Asn
export const ReturnBatchSelecion: React.FC<ReturnBatchSelecionProps> = ({
  handleStep,
  handleSnackbar,
  selectedOrderId,
  selectedAsnReturnDto,
  userId,
  setSelectedAsnId,
  setSelectedAsnNumber,
  setselectedAsnStateId,
  selectedWarehouseId,
  selectedTypeAsnIncomeId,
  selectedPreInboundId,
  selectedTrackingNumber,
  selectedCourier
}) => {
  const productDunModal = useModal<{
    relatedBatches: RelatedBatch[]
    row: OrderDetailReturnReasonsBatchInfoDto
    rowId: string
  }>('')
  // aallende
  const [selectedBatches, setSelectedBatches] = useState<SelectedBatchInfo[]>(
    []
  )

  // determina si se debe mostrar la grilla para selecicción de lotes. si existe algun sku con fefo la muestra. Ya que puede venir una devolución con sku con fefo y otro sku sin fefo.
  const [anyFefoEnabled, setAnyFefoEnabled] = useState<Boolean | undefined>(
    undefined
  )

  const { state: stateOrderDetailReturnReasonsBatch, callApi } =
    useApi<ApiResponse<OrderDetailReturnReasonsBatchInfoDto>>()
  const pagination = usePagination(5)
  const [selectedFilters] = useState<Filter[]>([])
  const [selectedSupplier, setSupplier] = useState(0)
  const [selectedSupplierOrigin, setSupplierOrigin] = useState(0)
  const { state: stateCreateASN, callApi: CreateasncallApi } = useApi()
  const [loading, setLoading] = useState(false)
  const [supplierError, setSupplierError] = useState('')

  // State para creación de Asn automatica
  const [autoSelectedSupplierOriginOk, setAutoSelectedSupplierOrigin] =
    useState(false)
  const [autoSelectedBatchOK, setAutoSelectedBatchOK] = useState(false)

  const [allowedWarehouseIds, setAllowedWarehouseIds] = useState<number>(0)
  const [productOriginId, setProductOriginId] = useState<number | undefined>()
  const [itemsWithUniqueId, setItemsWithUniqueId] = useState<
    OrderDetailWithId[]
  >([])

  const {
    suppliersState: { suppliers },
    getSuppliers
  } = useSuppliers()

  const {
    suppliersOriginState: { suppliersOrigin },
    getSuppliersOrigin
  } = useSuppliersOrigin()

  const requestAsnReturn = (preInboundId: number) => {
    callApi(AsnReturnOrderDetailReturnBatches(preInboundId))
      .then((response) => {
        const mappedItems: OrderDetailWithId[] = response.items.map(
          (item: OrderDetailReturnReasonsBatchInfoDto, index: number) => ({
            ...item,
            tempId: `${item.orderDetailId}_${index}`
          })
        )
        setItemsWithUniqueId(mappedItems)
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    pagination.setPagination(auxPage, pageSize)
  }

  const tableHeaders = generateTableHeaders(selectedBatches)

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestAsnReturn(selectedPreInboundId)
  }, [selectedFilters])

  useEffect(() => {
    if (!suppliers || suppliers.length === 0) {
      getSuppliers()
    }

    if (suppliersOrigin?.length === 0) {
      getSuppliersOrigin()
    }
  }, [])

  const validateSupplierForm = () => {
    return selectedSupplier <= 0 || selectedSupplierOrigin <= 0
  }

  const handleBatchSelected = (selectedBatch: SelectedBatchInfo) => {
    setSelectedBatches((prev: SelectedBatchInfo[]) => {
      // Filtrar por rowId en lugar de orderDetailId
      const filteredBatches = prev.filter(
        (batch) => batch.rowId !== selectedBatch.rowId
      )

      const newBatches = [...filteredBatches, selectedBatch]
      return newBatches
    })
  }

  const handleBatchSelectedDelete = (rowId: string) => {
    setSelectedBatches((prev) => prev.filter((item) => item.rowId !== rowId))
  }

  const convertRelatedBatchesToBatchListWithOrderId = (
    relatedBatches: RelatedBatch[]
  ) => {
    return relatedBatches.map((relatedBatch) => ({
      batchId: relatedBatch.batchId,
      batchNumber: relatedBatch.batchNumber,
      expirationDate: relatedBatch.expirationDate,
      orderDetailId: relatedBatch.orderDetailId
    }))
  }

  const convertSkuInfo = (orderItem?: OrderDetailReturnReasonsBatchInfoDto) => {
    return {
      skuId: orderItem?.skuId || 0,
      sku: orderItem?.sku || '',
      orderDetailId: orderItem?.orderDetailId || 0,
      hasBatch: true,
      hasExpirationDate: true
    }
  }

  const generateAsnDetails = (): AsnDetailsCreateDto[] => {
    if (!itemsWithUniqueId) return []

    return itemsWithUniqueId.map((orderDetail) => {
      const selectedBatch = selectedBatches.find(
        (batch) => batch.rowId === orderDetail.tempId
      )

      return {
        sku: orderDetail.sku,
        batchId: selectedBatch ? selectedBatch.batchId : null,
        batch: selectedBatch
          ? {
              batchNumber: selectedBatch.batchNumber,
              expirationDate: selectedBatch.expirationDate
            }
          : null,
        realQty: orderDetail.quantity,
        workOrderDetailId: orderDetail.workOrderDetailId,
        lpn: undefined,
        skuUnitCost: null
      }
    })
  }

  const generateOrderDetailReturnReasons =
    (): OrderDetailReturnReasonsCreateDto => {
      const returnReasonsDetails: OrderDetailReturnReasonCreateDto[] = []

      // Se valida si tenemos datos en nuestro estado local con IDs únicos
      if (!itemsWithUniqueId || itemsWithUniqueId.length === 0) {
        return { asnId: 0, returnReasonsDetails: [] }
      }

      // Iteramos sobre la totalidad de los items (independiente de la página actual)
      itemsWithUniqueId.forEach((item) => {
        // Si el item requiere lote (FEFO), buscamos su selección
        if (item.isFefoEnabled) {
          const selectedBatch = selectedBatches.find(
            (batch) => batch.rowId === item.tempId
          )

          if (selectedBatch) {
            returnReasonsDetails.push({
              orderDetailId: item.orderDetailId,
              returnReasonId: item.returnReasonId,
              batchId: selectedBatch.batchId,
              quantity: item.quantity
            })
          }
        } else {
          // Si NO es FEFO, igual debemos mandarlo pero con batchId en null
          returnReasonsDetails.push({
            orderDetailId: item.orderDetailId,
            returnReasonId: item.returnReasonId,
            batchId: null,
            quantity: item.quantity
          })
        }
      })

      return {
        asnId: 0,
        returnReasonsDetails
      }
    }

  const generateAsnNumber = (orderId: number, typeAsnIncomeId: number) => {
    if (typeAsnIncomeId === eTypeAsnIncome.Reverse) return `INV-${orderId}`

    if (typeAsnIncomeId === eTypeAsnIncome.UndeliverableOrder)
      return `IMP-${orderId}`

    return ''
  }

  const handleCreateAsn = () => {
    // Validación independiente
    if (validateSupplierForm()) return

    setLoading(true)
    const dataCreateAsn: AsnCreateDto = {
      asnNumber: generateAsnNumber(
        selectedAsnReturnDto?.orderId || selectedOrderId,
        selectedAsnReturnDto?.typeAsnIncomeId || selectedTypeAsnIncomeId
      ),
      asnStateId: eAsnState.Pending,
      warehouseId: selectedAsnReturnDto?.warehouseId || selectedWarehouseId,
      supplierId: selectedSupplier,
      supplierOriginId: selectedSupplierOrigin,
      typeAsnDocumentId: eTypeAsnDocument.Guía,
      typeAsnIncomeId:
        selectedAsnReturnDto?.typeAsnIncomeId || selectedTypeAsnIncomeId, // desde pre asn
      typeAsnSourceId: eTypeAsnSource.Manual,
      typeAsnprocessId: eTypeAsnProcess.Return,
      createdUserId: userId,
      courier: selectedAsnReturnDto?.courier || selectedCourier, // desde pre asn
      trackingNumber:
        selectedAsnReturnDto?.trackingNumber || selectedTrackingNumber, // desde pre asn
      asnDetails: generateAsnDetails(),
      preInboundId: selectedAsnReturnDto?.preInboundId || selectedPreInboundId, // desde pre asn
      orderDetailReturnReasons: generateOrderDetailReturnReasons()
    }

    CreateasncallApi(createAsn(dataCreateAsn))
      .then((t) => {
        setLoading(false)
        setSelectedAsnId(t.asnId)
        setSelectedAsnNumber(t.asnNumber)
        setselectedAsnStateId(t.asnStateId)
        handleStep(5)
      })
      .catch((e) => {
        setLoading(false)
        console.log(`error:`, e)
        handleSnackbar(`${e}`, false)
      })
  }

  const disableCreateButton = () => {
    if (!anyFefoEnabled) return false

    if (itemsWithUniqueId.length === 0) return true

    const fefoEnabledRows = itemsWithUniqueId.filter(
      (item) => item.isFefoEnabled === true
    )

    if (fefoEnabledRows.length === 0) return false

    const allFefoRowsSatisfied = fefoEnabledRows.every((row) =>
      selectedBatches.some((batch) => batch.rowId === row.tempId)
    )

    return !allFefoRowsSatisfied
  }

  const autoSelectSingleBatches = () => {
    if (itemsWithUniqueId.length === 0) return

    const newSelectedBatches: SelectedBatchInfo[] = []

    itemsWithUniqueId.forEach((orderDetail) => {
      if (
        orderDetail.relatedBatches &&
        orderDetail.relatedBatches.length === 1
      ) {
        const batch = orderDetail.relatedBatches[0]

        newSelectedBatches.push({
          rowId: orderDetail.tempId,
          orderDetailId: orderDetail.orderDetailId,
          batchId: batch.batchId,
          batchNumber: batch.batchNumber,
          expirationDate: batch.expirationDate
        })
      }
    })

    setSelectedBatches(newSelectedBatches)

    if (newSelectedBatches.length === itemsWithUniqueId.length) {
      setAutoSelectedBatchOK(true)
    }
  }

  useEffect(() => {
    if (itemsWithUniqueId.length > 0) {
      const anyFefoEnabled = itemsWithUniqueId.some(
        (item) => item.isFefoEnabled
      )

      if (anyFefoEnabled) {
        // Dispara la autoselección
        autoSelectSingleBatches()
      }

      getProductOrigin()
      setAnyFefoEnabled(anyFefoEnabled)
    } else {
      setAnyFefoEnabled(false)
    }
  }, [itemsWithUniqueId])

  // Verifica si los suppliers y supplierOrigin se actualizaron automaticamente
  // Verifica si los lotes se seleccionaron automaticamente
  // Si se cumplen estas dos condiciones se crea la Asn Automaticamente
  useEffect(() => {
    if (autoSelectedSupplierOriginOk === true) {
      // CON FEFO: cre asn automaticamente si  el proveedor, origen proveedor y seleccion de lotes (todos los skus 1 a N tengan lote asignado si es que solo tienen 1 lote) se hizo automaticamente
      if (anyFefoEnabled === true && autoSelectedBatchOK) {
        handleCreateAsn()
      }

      // SIN FEFO: Solo valida que la selección de supplier y supplier origin automatica sea 1 a 1 para los dos.
      if (anyFefoEnabled === false && autoSelectedSupplierOriginOk) {
        handleCreateAsn()
      }
    }
  }, [
    autoSelectedSupplierOriginOk,
    autoSelectedBatchOK,
    anyFefoEnabled,
    selectedBatches
  ])

  const getTableActions = (): Array<{
    key: string
    label: string
    onClick: (row: OrderDetailReturnReasonsBatchInfoDto, index?: number) => void
    icon: JSX.Element
    show?: (row: OrderDetailReturnReasonsBatchInfoDto) => boolean
  }> => {
    return [
      {
        key: 'selectedBatch',
        label: 'Asociar Lote',
        onClick: (row, index) => {
          const rowId = generateRowId(row, index || 0)
          productDunModal.openModal(
            'sm',
            { relatedBatches: row.relatedBatches, row, rowId },
            ''
          )
        },
        icon: <PlaylistAddOutlinedIcon />,
        show: (row) => row.isFefoEnabled === true
      },
      {
        key: 'deleteSelectedBatch',
        label: 'Eliminar selección Lote',
        onClick: (row, index) => {
          const rowId = generateRowId(row, index || 0)
          handleBatchSelectedDelete(rowId)
        },
        icon: <DeleteOutlineIcon />,
        show: (row) => row.isFefoEnabled === true
      },
      {
        key: 'infoBatch',
        label: 'SKU No requiere selección de lote (Sin Fefo)',
        onClick: () => {},
        icon: <InfoOutlinedIcon />,
        show: (row) => row.isFefoEnabled === false
      }
    ]
  }
  const getProductOrigin = () => {
    const poId =
      stateOrderDetailReturnReasonsBatch.data?.items?.find(
        (item) => item.productOriginId != null
      )?.productOriginId || undefined
    setProductOriginId(poId)

    // Obtenemos la bodega
    const warehouseId = getWarehouseId()
    // Obtenemos los proveedores relacionados a la bodega y la marca
    const suppliersAllowedAux =
      suppliers
        ?.filter(
          (s) =>
            s.warehouseIds.includes(warehouseId) && s.productOriginId === poId
        )
        .map((m) => m.supplierId) || []

    if (suppliersAllowedAux.length === 0)
      setSupplierError('No hay proveedor configurado, para Marca  y Bodega')

    // Si es solo uno el proveedor y el origen, se crea automaticamente
    if (suppliersAllowedAux.length === 1) {
      setSupplier(suppliersAllowedAux[0])

      const suppOriginAux =
        suppliersOrigin
          ?.filter(
            (item) => item.active && item.supplierId === suppliersAllowedAux[0]
          )
          .map((m) => m.id) || []

      // si es solo un origen, se envia a crear la ASN
      if (suppOriginAux.length === 1) {
        setSupplierOrigin(suppOriginAux[0])
        setAutoSelectedSupplierOrigin(true)
      }
    }

    return poId
  }

  const getWarehouseId = () => {
    const warehouseId = selectedAsnReturnDto?.warehouseId || selectedWarehouseId
    setAllowedWarehouseIds(warehouseId)

    return warehouseId
  }

  if (
    stateOrderDetailReturnReasonsBatch.status === Status.Loading ||
    stateCreateASN.status === Status.Loading
  ) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  const fullData = itemsWithUniqueId || []

  const paginatedData = fullData.slice(
    pagination.page * pagination.pageSize,
    (pagination.page + 1) * pagination.pageSize
  )

  return (
    <Fragment>
      <Paper style={{ padding: '16px', marginBottom: '16px' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant='h6'>Proveedor:</Typography>
            <SuppliersDDL
              onChange={(e: any) => setSupplier(Number(e.target.value))}
              value={selectedSupplier}
              type='outlined'
              fullWidth={true}
              allowedWarehouseIds={[allowedWarehouseIds]}
              productOriginId={productOriginId}
              error={supplierError.length > 0}
              errorText={supplierError}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant='h6'>Origen de proveedor:</Typography>
            <SupplierOriginDDL
              onChange={(e: any) => setSupplierOrigin(e.target.value as number)}
              value={selectedSupplierOrigin}
              type='outlined'
              fullWidth={true}
              supplierId={selectedSupplier}
              required={true}
            />
          </Grid>
        </Grid>
      </Paper>
      <Grid container spacing={2}>
        {anyFefoEnabled && (
          <Fragment>
            <Grid size={{ xs: 12, md: 10 }}>
              <Typography variant='body2'>Selección de Lote:</Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <GenericTableV2<OrderDetailWithId>
                styling={{
                  dense: false
                }}
                columns={(tableHeaders as any) || []}
                keyField={'tempId'}
                data={paginatedData}
                pagination={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  total: fullData.length,
                  onPageChange: handlePaginationChange
                }}
                actions={getTableActions()}
              />
            </Grid>
          </Fragment>
        )}

        <Grid size={{ md: 10 }}></Grid>
        <Grid size={{ xs: 12, md: 2 }} style={{ paddingTop: '17px' }}>
          <Box display='flex' alignItems='center'>
            <LoadingButton
              loading={loading}
              variant='contained'
              color='primary'
              onClick={() => handleCreateAsn()}
              disabled={disableCreateButton()}
            >
              Crear Asn
            </LoadingButton>
            {loading && (
              <Typography
                variant='body2'
                style={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}
              >
                Creando Asn...
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      <Modal
        open={productDunModal.isOpen}
        onClose={productDunModal.closeModal}
        title={productDunModal.title}
        maxWidth={productDunModal.size}
      >
        <SimpleBatchSelector
          handleCloseModal={productDunModal.closeModal}
          batchList={convertRelatedBatchesToBatchListWithOrderId(
            productDunModal.extendedProps?.relatedBatches || []
          )}
          selectedSku={{
            ...convertSkuInfo(productDunModal.extendedProps?.row),
            rowId: productDunModal.extendedProps?.rowId // Pasa el rowId
          }}
          onBatchSelected={handleBatchSelected}
        />
      </Modal>
    </Fragment>
  )
}
